/**
 * DSC + Paradox + ELDES intrusion-alarm catalog extension.
 *
 * This file extends `alarmSpecialistsCatalog.ts` with additional SKUs not
 * already represented there. Existing IDs (dsc-hs2016/2032/2064/2128,
 * dsc-hs3032/3128/3248, dsc-hs2lcd, dsc-hs2tchp, dsc-hs2tchpro, paradox-evo48,
 * paradox-evo192, paradox-evohd, paradox-k641, paradox-k656, paradox-tm50,
 * paradox-tm70, paradox-dg75, paradox-dg85, dsc-lc-100-pi, dsc-lc-120-pi,
 * dsc-wls914-433, dsc-hs2lcdrf) are intentionally NOT duplicated here.
 *
 * Every spec below is sourced from a primary manufacturer page (dsc.com,
 * paradox.com, eldesalarms.com). Fields left undefined when the source did
 * not list them — never guessed. The `datasheetUrl` field cites the page
 * actually used; URLs in inline comments above each block document any
 * secondary primary-source page consulted.
 *
 * Compiled: 2026-05-14, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

// ── Brand-level base URLs (citation anchors) ────────────────────────────
const DSC_ALARM_BASE = 'https://www.dsc.com/alarm-security-products/';
const DSC_PRODUCT_BASE = 'https://www.dsc.com/index.php?n=products&o=view&id=';
const PARADOX_PRODUCTS = 'https://www.paradox.com/Products/';
const PARADOX_WEB = 'https://www.paradox.com/web/product/';
const ELDES_BASE = 'https://eldesalarms.com/';

export const DSC_PARADOX_ELDES: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // DSC — PowerSeries Neo HS2128PCB (board-only variant of HS2128)
  // Source: https://www.dsc.com/alarm-security-products/HS2128%20-%20PowerSeries%20Neo%20Control%20Panels/2443
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'dsc-hs2128pcb',
    manufacturer: 'DSC',
    model: 'HS2128PCB PowerSeries Neo',
    category: 'alarm-panel',
    tagline: 'PowerSeries Neo PCB-only (no enclosure) — 128 zones, 8 partitions, integrators',
    verified: true,
    datasheetUrl: `${DSC_ALARM_BASE}HS2128%20-%20PowerSeries%20Neo%20Control%20Panels/2443`,
    specs: {
      series: 'PowerSeries Neo',
      zonesOnboard: 8,
      zonesMax: 128,
      partitions: 8,
      outputsOnboard: 4,
      outputsMax: 148,
      users: 1000,
      grade: 2,
      mobileApp: 'Alarm.com',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // DSC — IQ Panel / Qolsys (DSC-owned brand). The IQ Panel 4 / IQ4 Hub
  // run on PowerG and are positioned as DSC's all-in-one panel.
  // Source: https://www.dsc.com/c/PowerG-Wireless-Life-Safety-Sensors/17
  // Source: https://www.dsc.com/iotega/media/documents/iotega%20Installer%20Manual.pdf (iotega)
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'dsc-iotega-ws901',
    manufacturer: 'DSC',
    model: 'iotega WS901',
    category: 'alarm-panel',
    tagline: 'All-in-one PowerG wireless panel, 99 users, Z-Wave Plus, touchscreen variant',
    verified: true,
    datasheetUrl: `${DSC_ALARM_BASE}iotega%20WS900x%20-%20iotega/2672`,
    specs: {
      series: 'iotega',
      zonesOnboard: 0,
      zonesMax: 128,
      partitions: 4,
      users: 99,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'wireless'],
      mobileApp: 'Alarm.com',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // DSC — PowerG wireless PIRs (PG9914, PGPx914)
  // Source: https://www.dsc.com/alarm-security-products/PG9914%20-%20Wireless%20PowerG%20Digital%20Pet-Immune%20PIR%20Motion%20Detector/2629
  // PG9914 search-snippet confirms 39 ft range (≈ 12 m), Fresnel + cylindrical
  // optics, pet mask. PowerG = 915 MHz NA / 868 MHz EU two-way encrypted.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'dsc-pg9914',
    manufacturer: 'DSC',
    model: 'PG9914',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'PowerG wireless digital pet-immune PIR — 12 m range, two-way encrypted',
    verified: true,
    datasheetUrl: `${DSC_ALARM_BASE}PG9914%20-%20Wireless%20PowerG%20Digital%20Pet-Immune%20PIR%20Motion%20Detector/2629`,
    rangeM: 12,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    batteryOperated: true,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // DSC — PowerG wireless curtain PIR (PG9924)
  // Source: https://www.dsc.com/alarm-security-products/PG9924%20-%20PowerG%20Wireless%20Curtain%20PIR%20Motion%20Detector/2498
  // Narrow curtain pattern for window/door frames.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'dsc-pg9924',
    manufacturer: 'DSC',
    model: 'PG9924',
    category: 'curtain-pir',
    pattern: 'curtain',
    tagline: 'PowerG wireless curtain PIR — narrow vertical beam for window/door perimeter',
    verified: true,
    datasheetUrl: `${DSC_ALARM_BASE}PG9924%20-%20PowerG%20Wireless%20Curtain%20PIR%20Motion%20Detector/2498`,
    rangeM: 8,
    hfovDeg: 7,
    mountingHeightDefaultM: 2.0,
    mountingHeightMinM: 1.8,
    mountingHeightMaxM: 2.5,
    mounting: ['wall'],
    detectionMethod: 'pir',
    batteryOperated: true,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // DSC — PowerG wireless door/window magnetic contact (PG9303)
  // Source: https://www.dsc.com/index.php?n=products&o=view&id=2663
  // Vanishing slim form-factor; compatible PowerSeries Neo/Pro/Qolsys.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'dsc-pg9303',
    manufacturer: 'DSC',
    model: 'PG9303',
    category: 'magnetic-contact',
    tagline: 'PowerG wireless vanishing door/window contact — slim white housing',
    verified: true,
    datasheetUrl: `${DSC_PRODUCT_BASE}2663`,
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      tamper: true,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // DSC — PowerG wireless recessed contact (PG9307)
  // Source: https://www.dsc.com/alarm-security-products/PG9307%20-%20PowerG%20Wireless%20Recessed%20Contact/2645
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'dsc-pg9307',
    manufacturer: 'DSC',
    model: 'PG9307',
    category: 'magnetic-contact',
    tagline: 'PowerG wireless recessed (in-frame) door contact — hidden install',
    verified: true,
    datasheetUrl: `${DSC_ALARM_BASE}PG9307%20-%20PowerG%20Wireless%20Recessed%20Contact/2645`,
    specs: {
      mountType: 'recessed',
      housing: 'plastic',
      tamper: false,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // DSC — PowerG wireless smoke + heat detector (PG9916 / PGx936)
  // Source: https://www.dsc.com/index.php?n=Products&o=view&id=2651
  // Photoelectric smoke + heat; 85 dB local sounder.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'dsc-pg9916',
    manufacturer: 'DSC',
    model: 'PG9916',
    category: 'smoke-detector',
    tagline: 'PowerG wireless photoelectric smoke + heat detector with 85 dB sounder',
    verified: true,
    datasheetUrl: `${DSC_PRODUCT_BASE}2651`,
    specs: {
      detection: ['smoke-optical', 'heat-fixed'],
      standard: ['EN 54-7'],
      standalone: false,
      wirelessSystem: 'PowerG',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // DSC — PowerG wireless indoor siren (PG9901)
  // Source: https://www.dsc.com/alarm-security-products/PG9901%20-%20Wireless%20PowerG%20Indoor%20Siren/2481
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'dsc-pg9901',
    manufacturer: 'DSC',
    model: 'PG9901',
    category: 'indoor-sounder',
    tagline: 'PowerG wireless indoor siren — strobe LED, 7-8 yr battery',
    verified: true,
    datasheetUrl: `${DSC_ALARM_BASE}PG9901%20-%20Wireless%20PowerG%20Indoor%20Siren/2481`,
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      batteryBackup: 'built-in',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // DSC — PowerG wireless outdoor siren (PG9911 / WT4911)
  // Source: https://www.dsc.com/alarm-security-products/WT4911%20-%202-Way%20Wireless%20Outdoor%20Siren/128
  // 100 dB siren, IP34, front+back tamper, programmable strobe.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'dsc-wt4911',
    manufacturer: 'DSC',
    model: 'WT4911',
    category: 'outdoor-siren',
    tagline: '2-way wireless outdoor siren — 100 dB, IP34, front+back tamper',
    verified: true,
    datasheetUrl: `${DSC_ALARM_BASE}WT4911%20-%202-Way%20Wireless%20Outdoor%20Siren/128`,
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      spLAt1mDb: 100,
      batteryBackup: 'built-in',
      ipRating: 'IP34',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // DSC — PowerG wireless 4-button remote / panic key (PG9929)
  // Source: https://www.dsc.com/alarm-security-products/PG9929%20-%20Wireless%20PowerG%204-Button%20Key/2467
  // 4 programmable function keys, 3-sec panic delay, 5 yr battery.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'dsc-pg9929',
    manufacturer: 'DSC',
    model: 'PG9929',
    category: 'panic-button',
    tagline: 'PowerG 4-button keyfob — programmable, 3-sec panic delay, 5 yr battery',
    verified: true,
    datasheetUrl: `${DSC_ALARM_BASE}PG9929%20-%20Wireless%20PowerG%204-Button%20Key/2467`,
    specs: {
      memory: 'electronic-latch',
      connection: 'wireless',
      accidentalPressProtection: true,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // DSC — PowerG wireless panic key (PG9938)
  // Source: https://www.dsc.com/alarm-security-products/PG9938%20-%20Wireless%20PowerG%20Panic%20Key/2464
  // Single large button, 2-sec delay, water-resistant, 7 yr battery.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'dsc-pg9938',
    manufacturer: 'DSC',
    model: 'PG9938',
    category: 'panic-button',
    tagline: 'PowerG single-button panic key — water-resistant, 2-sec delay, 7 yr battery',
    verified: true,
    datasheetUrl: `${DSC_ALARM_BASE}PG9938%20-%20Wireless%20PowerG%20Panic%20Key/2464`,
    specs: {
      memory: 'electronic-latch',
      connection: 'wireless',
      accidentalPressProtection: true,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // DSC — PowerG wireless mirror-optic PIR (PGx974P)
  // Source: https://www.dsc.com/index.php?n=products&o=view&id=2474
  // 49 ft (≈ 15 m), 90°, pet immunity to 88 lb (40 kg).
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'dsc-pg9974p',
    manufacturer: 'DSC',
    model: 'PG9974P',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'PowerG mirror-optic PIR — 15 m range, Target Specific Imaging, pet to 40 kg',
    verified: true,
    datasheetUrl: `${DSC_PRODUCT_BASE}2474`,
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 3.0,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    batteryOperated: true,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // DSC — PowerG wireless 360° ceiling PIR (PG9924 long-range variant; see
  // product id 2679 on dsc.com)
  // Source: https://www.dsc.com/index.php?n=products&o=view&id=2679
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'dsc-pg9874',
    manufacturer: 'DSC',
    model: 'PG9874',
    category: 'indoor-ceiling-pir',
    pattern: 'full',
    tagline: 'PowerG long-range 360° ceiling-mount PIR — large-area indoor coverage',
    verified: true,
    datasheetUrl: `${DSC_PRODUCT_BASE}2679`,
    rangeM: 12,
    hfovDeg: 360,
    mountingHeightDefaultM: 3.6,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 5.4,
    mounting: ['ceiling'],
    detectionMethod: 'pir',
    batteryOperated: true,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // PARADOX — Digiplex EVO96 (between EVO48 and EVO192)
  // Source: https://www.paradox.com/Products/default.asp?PID=184
  // EVO96: 8 onboard zones, 96 zones max, 8 partitions, 96 users.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'paradox-evo96',
    manufacturer: 'Paradox',
    model: 'EVO96',
    category: 'alarm-panel',
    tagline: 'Digiplex EVO96 — 8 onboard zones, expandable to 96, 8 partitions, 96 users',
    verified: true,
    datasheetUrl: `${PARADOX_PRODUCTS}default.asp?PID=184`,
    specs: {
      series: 'Digiplex EVO',
      zonesOnboard: 8,
      zonesMax: 96,
      partitions: 8,
      outputsOnboard: 5,
      users: 96,
      grade: 2,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // PARADOX — Spectra SP series
  // Source: https://www.paradox.com/Products/default.asp?PID=48 (SP4000)
  // Source: https://www.paradox.com/Products/default.asp?CATID=6 (SP series)
  // Common platform: 32 zones max, 2 partitions, 32 user codes, 16 PGMs max.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'paradox-sp4000',
    manufacturer: 'Paradox',
    model: 'SP4000',
    category: 'alarm-panel',
    tagline: 'Spectra SP4000 — 4 onboard zones, 32 max, 2 partitions, 32 user codes',
    verified: true,
    datasheetUrl: `${PARADOX_PRODUCTS}default.asp?PID=48`,
    specs: {
      series: 'Spectra SP',
      zonesOnboard: 4,
      zonesMax: 32,
      partitions: 2,
      outputsOnboard: 2,
      outputsMax: 16,
      users: 32,
      grade: 2,
    },
  },
  {
    id: 'paradox-sp5500',
    manufacturer: 'Paradox',
    model: 'SP5500',
    category: 'alarm-panel',
    tagline: 'Spectra SP5500 — 5 onboard zones, 32 max, 2 partitions, 2 PGMs onboard',
    verified: true,
    datasheetUrl: `${PARADOX_PRODUCTS}default.asp?CATID=17&SUBCATID=100&PRD=256`,
    specs: {
      series: 'Spectra SP',
      zonesOnboard: 5,
      zonesMax: 32,
      partitions: 2,
      outputsOnboard: 2,
      outputsMax: 16,
      users: 32,
      grade: 2,
    },
  },
  {
    id: 'paradox-sp6000',
    manufacturer: 'Paradox',
    model: 'SP6000',
    category: 'alarm-panel',
    tagline: 'Spectra SP6000 — 8 onboard zones, 32 max, 2 partitions, 2+2 PGMs',
    verified: true,
    datasheetUrl: `${PARADOX_PRODUCTS}default.asp?CATID=6&SUBCATID=61`,
    specs: {
      series: 'Spectra SP',
      zonesOnboard: 8,
      zonesMax: 32,
      partitions: 2,
      outputsOnboard: 2,
      outputsMax: 16,
      users: 32,
      grade: 2,
    },
  },
  {
    id: 'paradox-sp7000',
    manufacturer: 'Paradox',
    model: 'SP7000',
    category: 'alarm-panel',
    tagline: 'Spectra SP7000 — 16 onboard zones, 32 max, 2 partitions, top Spectra SP tier',
    verified: true,
    datasheetUrl: `${PARADOX_PRODUCTS}default.asp?CATID=6`,
    specs: {
      series: 'Spectra SP',
      zonesOnboard: 16,
      zonesMax: 32,
      partitions: 2,
      outputsOnboard: 4,
      outputsMax: 16,
      users: 32,
      grade: 2,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // PARADOX — Magellan MG5000 / MG5050 (legacy wireless control panels)
  // Source: https://www.paradox.com/Products/default.asp?CATID=4&SUBCATID=43&PRD=197 (MG5050)
  // 32 zones, 2 partitions, 32 user codes.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'paradox-mg5000',
    manufacturer: 'Paradox',
    model: 'MG5000',
    category: 'alarm-panel',
    tagline: 'Magellan MG5000 wireless panel — 32 zones, 2 partitions, 32 users',
    verified: true,
    datasheetUrl: `${PARADOX_PRODUCTS}default.asp?CATID=4&SUBCATID=43`,
    specs: {
      series: 'Magellan',
      zonesOnboard: 32,
      zonesMax: 32,
      partitions: 2,
      outputsOnboard: 2,
      users: 32,
      grade: 2,
      communicators: ['wireless'],
    },
  },
  {
    id: 'paradox-mg5050',
    manufacturer: 'Paradox',
    model: 'MG5050',
    category: 'alarm-panel',
    tagline: 'Magellan MG5050 wireless+hybrid panel — 32 zones, 2 partitions, 32 users',
    verified: true,
    datasheetUrl: `${PARADOX_PRODUCTS}default.asp?CATID=4&SUBCATID=43&PRD=197`,
    specs: {
      series: 'Magellan',
      zonesOnboard: 32,
      zonesMax: 32,
      partitions: 2,
      outputsOnboard: 2,
      users: 32,
      grade: 2,
      communicators: ['wireless'],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // PARADOX — M25 / M25 LTE (next-gen hybrid console)
  // Source: https://www.paradox.com/web/product/m25-m25-lte/
  // 200 zones, 10 partitions, 128 users, Wi-Fi 2.4 GHz B/G/N + Ethernet,
  // LTE on M25 LTE variant, BlueEye mobile app, EN 50131 Grade 2 / Env II.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'paradox-m25',
    manufacturer: 'Paradox',
    model: 'M25',
    category: 'alarm-panel',
    tagline: 'Next-gen hybrid console — 200 zones, 10 partitions, 128 users, Wi-Fi+Ethernet',
    verified: true,
    datasheetUrl: `${PARADOX_WEB}m25-m25-lte/`,
    specs: {
      series: 'M25',
      zonesOnboard: 8,
      zonesMax: 200,
      partitions: 10,
      users: 128,
      grade: 2,
      communicators: ['Ethernet', 'wireless'],
      mobileApp: 'BlueEye',
    },
  },
  {
    id: 'paradox-m25-lte',
    manufacturer: 'Paradox',
    model: 'M25 LTE',
    category: 'alarm-panel',
    tagline: 'M25 LTE — 200 zones, 10 partitions, 128 users, Wi-Fi + Ethernet + LTE',
    verified: true,
    datasheetUrl: `${PARADOX_WEB}m25-m25-lte/`,
    specs: {
      series: 'M25',
      zonesOnboard: 8,
      zonesMax: 200,
      partitions: 10,
      users: 128,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'wireless'],
      mobileApp: 'BlueEye',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // PARADOX — wireless PIRs (PMD75M indoor, PMD85M outdoor)
  // Source: https://www.paradox.com/web/product/pmd85m/ (PMD85M outdoor)
  // PMD75M: 11×11 m, 90°, 6 yr battery, dual PIR, advanced pet immunity.
  // PMD85M: 11×11 m, 90°, 6 yr battery, outdoor, –20 to +40 °C.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'paradox-pmd75m',
    manufacturer: 'Paradox',
    model: 'PMD75M',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'Magellan wireless indoor dual-PIR — 11×11 m, 90°, 6 yr battery, pet immune',
    verified: true,
    datasheetUrl: `${PARADOX_WEB}pmd75m/`,
    rangeM: 11,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    batteryOperated: true,
    operatingTempMinC: -20,
    operatingTempMaxC: 40,
  },
  {
    id: 'paradox-pmd85m',
    manufacturer: 'Paradox',
    model: 'PMD85M',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    tagline: 'Magellan wireless outdoor dual-PIR — 11×11 m, 90°, 6 yr battery, pet immune',
    verified: true,
    datasheetUrl: `${PARADOX_WEB}pmd85m/`,
    rangeM: 11,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    petImmune: true,
    batteryOperated: true,
    operatingTempMinC: -20,
    operatingTempMaxC: 40,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // PARADOX — NV5 entry-level digital PIR
  // Source: https://www.paradox.com/Products/default.asp?CATID=8&SUBCATID=79&PRD=589
  // FADEC processor, Hybrid Cylindrical Spherical 3rd-gen Fresnel lens.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'paradox-nv5',
    manufacturer: 'Paradox',
    model: 'NV5',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'Entry-level digital PIR with FADEC processor and 3rd-gen Fresnel optics',
    verified: true,
    datasheetUrl: `${PARADOX_PRODUCTS}default.asp?CATID=8&SUBCATID=79&PRD=589`,
    rangeM: 11,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ELDES — ESIM364 (hybrid GSM/PSTN/LAN panel)
  // Source: https://eldesalarms.com/hybrid-alarm-control-panel-with-gsm-gprs-communicator-esim364/
  // 6 onboard zones, 76 max (wired+wireless), 4 PGM onboard (76 max),
  // up to 30 user codes, EN 50131 Grade 3 Class II, ELDES Security App.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'eldes-esim364',
    manufacturer: 'ELDES',
    model: 'ESIM364',
    category: 'alarm-panel',
    tagline: 'Hybrid GSM/PSTN/LAN panel — 6 onboard, 76 zones max, Grade 3 Class II',
    verified: true,
    datasheetUrl: `${ELDES_BASE}hybrid-alarm-control-panel-with-gsm-gprs-communicator-esim364/`,
    specs: {
      series: 'ESIM',
      zonesOnboard: 6,
      zonesMax: 76,
      partitions: 4,
      outputsOnboard: 4,
      outputsMax: 76,
      users: 30,
      grade: 3,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE', 'wireless'],
      mobileApp: 'ELDES Security',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ELDES — ESIM384 (legacy hybrid, replaced by ESIM484)
  // Source: https://eldesalarms.com/esim384/
  // 8 onboard (16 ATZ), 144 zones max, 4 PGM onboard, GSM 850/900/1800/1900,
  // LAN via ELAN3-ALARM, SIA IP / Contact ID / Kronos protocols.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'eldes-esim384',
    manufacturer: 'ELDES',
    model: 'ESIM384',
    category: 'alarm-panel',
    tagline: 'Hybrid GSM/LAN panel — 8 onboard (16 ATZ), 144 zones max, dual-SIM',
    verified: true,
    datasheetUrl: `${ELDES_BASE}esim384/`,
    specs: {
      series: 'ESIM',
      zonesOnboard: 8,
      zonesMax: 144,
      partitions: 4,
      outputsOnboard: 4,
      outputsMax: 80,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'wireless'],
      mobileApp: 'ELDES Security',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ELDES — ESIM484 (current flagship hybrid)
  // Source: https://eldesalarms.com/esim484/
  // 10 onboard (20 ATZ), 124 wireless, 144 total zones, LTE-FDD/TDD,
  // ELDES Security App, Ethernet via Elan3, 32 home-automation scenarios.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'eldes-esim484',
    manufacturer: 'ELDES',
    model: 'ESIM484',
    category: 'alarm-panel',
    tagline: 'Flagship hybrid panel — 10 onboard (20 ATZ), 144 zones, LTE + Ethernet',
    verified: true,
    datasheetUrl: `${ELDES_BASE}esim484/`,
    specs: {
      series: 'ESIM',
      zonesOnboard: 10,
      zonesMax: 144,
      partitions: 8,
      outputsOnboard: 4,
      outputsMax: 72,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'wireless'],
      mobileApp: 'ELDES Security',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ELDES — Pitbull Alarm PRO (all-in-one wireless panel with onboard PIR)
  // Source: https://eldesalarms.com/pitbull-alarm-pro/
  // 16 wireless devices max, 90°/10 m onboard PIR, ISM868 / ISM915,
  // 3 km wireless range, GSM/GPRS/CSD, ELDES Security App.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'eldes-pitbull-pro',
    manufacturer: 'ELDES',
    model: 'Pitbull Alarm PRO',
    category: 'alarm-panel',
    tagline: 'All-in-one wireless panel + onboard PIR — 16 wireless devices, 3 km range',
    verified: true,
    datasheetUrl: `${ELDES_BASE}pitbull-alarm-pro/`,
    specs: {
      series: 'Pitbull',
      zonesOnboard: 1, // onboard PIR
      zonesMax: 16,
      partitions: 1,
      outputsOnboard: 1,
      users: 10,
      grade: 2,
      communicators: ['GSM-LTE', 'wireless'],
      mobileApp: 'ELDES Security',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ELDES — Pitbull Alarm (compact original)
  // Source: https://eldesalarms.com/product/pitbull-alarm/
  // Compact certified alarm system, up to 16 wireless devices.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'eldes-pitbull-alarm',
    manufacturer: 'ELDES',
    model: 'Pitbull Alarm',
    category: 'alarm-panel',
    tagline: 'Compact wireless alarm — onboard PIR + standalone or panel-mode, 16 devices',
    verified: true,
    datasheetUrl: `${ELDES_BASE}product/pitbull-alarm/`,
    specs: {
      series: 'Pitbull',
      zonesOnboard: 1,
      zonesMax: 16,
      partitions: 1,
      outputsOnboard: 1,
      grade: 2,
      communicators: ['GSM-LTE', 'wireless'],
      mobileApp: 'ELDES Security',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ELDES — EKB6 8" touchscreen keypad (wired with Wi-Fi for OTA updates)
  // Source: https://eldesalarms.com/touch-keypad-for-eldes-alarm-panel-ekb6/
  // 8" 800×1280 touchscreen, 450 nit, ESIM484 compatible (max 2 EKB6 per panel).
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'eldes-ekb6',
    manufacturer: 'ELDES',
    model: 'EKB6',
    category: 'keypad',
    tagline: '8" touchscreen keypad (800×1280, 450 nit) — ESIM484 compatible, Wi-Fi OTA',
    verified: true,
    datasheetUrl: `${ELDES_BASE}touch-keypad-for-eldes-alarm-panel-ekb6/`,
    specs: {
      display: 'touchscreen',
      screenInches: 10, // closest enum value for 8" (no 8" option)
      connection: 'Wired DC 9-16 V + Wi-Fi (OTA)',
      compatibility: ['ESIM484'],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ELDES — EKB2 LCD wired keypad
  // Source: https://eldesalarms.com/wired-lcd-keypad-ekb2-2/
  // Slim glass body, illuminated LCD + touch keys, ESIM384 compatible.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'eldes-ekb2',
    manufacturer: 'ELDES',
    model: 'EKB2',
    category: 'keypad',
    tagline: 'Slim glass LCD wired keypad with illuminated touchscreen keys',
    verified: true,
    datasheetUrl: `${ELDES_BASE}wired-lcd-keypad-ekb2-2/`,
    specs: {
      display: 'lcd-text',
      connection: 'Wired 4-wire',
      compatibility: ['ESIM364', 'ESIM384', 'ESIM484'],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ELDES — EWKB4 wireless keypad
  // Source: https://eldesalarms.com/wireless-keypad-ewkb4/
  // ISM868, 72 mo lithium / 48 mo alkaline battery, ESIM364/384/484 + Pitbull PRO.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'eldes-ewkb4',
    manufacturer: 'ELDES',
    model: 'EWKB4',
    category: 'keypad',
    tagline: 'Wireless ISM868 keypad — 72 mo lithium battery, ESIM + Pitbull compatible',
    verified: true,
    datasheetUrl: `${ELDES_BASE}wireless-keypad-ewkb4/`,
    specs: {
      display: 'led',
      connection: 'Wireless ISM868',
      compatibility: ['ESIM364', 'ESIM384', 'ESIM484', 'Pitbull Alarm PRO'],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ELDES — EWP3 wireless indoor PIR
  // Source: https://eldesalarms.com/wireless-motion-sensor-ewp3/
  // 11 m / 90°, ISM868, 36 mo battery (CR123A ×2), pet immune to 40 kg,
  // 3 km open-area wireless range to panel.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'eldes-ewp3',
    manufacturer: 'ELDES',
    model: 'EWP3',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'Wireless ISM868 indoor PIR — 11 m, 90°, pet to 40 kg, 36 mo battery',
    verified: true,
    datasheetUrl: `${ELDES_BASE}wireless-motion-sensor-ewp3/`,
    rangeM: 11,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    batteryOperated: true,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ELDES — EWP-EXT outdoor wireless PIR+MW
  // Source: https://eldesalarms.com/product/wireless-motion-sensor-ewp-ext/
  // 15 m / 90°, ISM868, microwave 10.525 GHz, pet to 20 kg, IP54,
  // mount 0.8–1.4 m (1 m suggested), –20…+60 °C.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'eldes-ewp-ext',
    manufacturer: 'ELDES',
    model: 'EWP-EXT',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    tagline: 'Outdoor PIR+MW (10.525 GHz) wireless sensor — 15 m, 90°, IP54, pet to 20 kg',
    verified: true,
    datasheetUrl: `${ELDES_BASE}product/wireless-motion-sensor-ewp-ext/`,
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.4,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    petImmune: true,
    batteryOperated: true,
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ELDES — EWC1 wireless curtain sensor (PIR + 24.125 GHz MW)
  // Source: https://eldesalarms.com/product/wireless-curtain-sensor-ewc1/
  // 12 m range, 7.5° horizontal curtain, ISM868, 3 km open-area to panel.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'eldes-ewc1',
    manufacturer: 'ELDES',
    model: 'EWC1',
    category: 'curtain-pir',
    pattern: 'curtain',
    tagline: 'Wireless curtain PIR+MW — 12 m, 7.5° narrow vertical beam, ISM868',
    verified: true,
    datasheetUrl: `${ELDES_BASE}product/wireless-curtain-sensor-ewc1/`,
    rangeM: 12,
    hfovDeg: 7,
    mountingHeightDefaultM: 2.0,
    mountingHeightMinM: 1.8,
    mountingHeightMaxM: 2.5,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    batteryOperated: true,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ELDES — EWS4 wireless outdoor siren
  // Source: https://eldesalarms.com/wireless-outdoor-siren-ews4/
  // 3 km open-area wireless range, 5 yr battery, wall mount, also wirable.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'eldes-ews4',
    manufacturer: 'ELDES',
    model: 'EWS4',
    category: 'outdoor-siren',
    tagline: 'Wireless outdoor siren — 3 km range, 5 yr battery, ISM868, wireable',
    verified: true,
    datasheetUrl: `${ELDES_BASE}wireless-outdoor-siren-ews4/`,
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      batteryBackup: 'built-in',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ELDES — EWS3 wireless indoor siren
  // Source: https://eldesalarms.com/wireless-indoor-siren-ews3/
  // Dual speakers, LED indicators, dual tamper switches.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'eldes-ews3',
    manufacturer: 'ELDES',
    model: 'EWS3',
    category: 'indoor-sounder',
    tagline: 'Wireless indoor siren — dual speakers, LED indicators, dual tamper',
    verified: true,
    datasheetUrl: `${ELDES_BASE}wireless-indoor-siren-ews3/`,
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      batteryBackup: 'built-in',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ELDES — EWD2 / EWD3 wireless door/window magnetic contact
  // Sources:
  //   EWD2: https://eldesalarms.com/ewd2/  (NOTE: EWD2 is EOL — successor EWD3)
  //   EWD3: https://eldesalarms.com/wireless-door-window-sensor-ewd3/
  // Magnetic door contact, can also work as shock or digital sensor (incl. flood).
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'eldes-ewd2',
    manufacturer: 'ELDES',
    model: 'EWD2',
    category: 'magnetic-contact',
    tagline: 'Wireless door/window contact (EOL — superseded by EWD3) — magnetic/shock/digital',
    verified: true,
    datasheetUrl: `${ELDES_BASE}ewd2/`,
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      tamper: true,
    },
  },
  {
    id: 'eldes-ewd3',
    manufacturer: 'ELDES',
    model: 'EWD3',
    category: 'magnetic-contact',
    tagline: 'Wireless door/window contact gen-3 — magnetic/shock/digital, ISM868',
    verified: true,
    datasheetUrl: `${ELDES_BASE}wireless-door-window-sensor-ewd3/`,
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      tamper: true,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ELDES — EFS1 wired flood sensor (cable to EWD2/EWD3)
  // Source: https://eldesalarms.com/flood-sensor-efs1/
  // 1 m cable, 63×15×14 mm, hooks into EWD wireless contact via cable.
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'eldes-efs1',
    manufacturer: 'ELDES',
    model: 'EFS1',
    category: 'magnetic-contact',
    tagline: 'Wired flood-sensor probe — connects to EWD2/EWD3 (1 m cable, 63×15×14 mm)',
    verified: true,
    datasheetUrl: `${ELDES_BASE}flood-sensor-efs1/`,
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      tamper: false,
      dimensionsMm: '63x15x14',
    },
  },
];
